import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { 
  LayoutDashboard, 
  Building2, 
  Users, 
  FileText, 
  Link2, 
  Tags,
  Settings,
  BookOpen,
  ClipboardList,
  LogOut,
  ExternalLink,
  Image,
  Shield,
  ShieldCheck,
  FileCode,
  Sparkles,
  CalendarClock,
  FolderOpen,
  Globe,
  Target,
  GitBranch,
  Newspaper,
  Lightbulb,
  UserCircle,
  Palette,
  AlertTriangle,
  Package,
  Book,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  Menu,
  MessageSquare,
  LayoutTemplate,
  Megaphone,
  Upload,
  CalendarDays,
  Rocket,
  Send,
  GitMerge,
  BarChart2,
  Rss,
  Layers,
  Mail,
  Handshake,
  TrendingUp,
  Star,
  Navigation,
} from "lucide-react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DevRoleSwitcher } from "@/components/dev-role-switcher";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import type { SidebarMenuGroup, SidebarMenuItem as SidebarMenuItemType } from "@shared/schema";

const ICON_MAP: Record<string, any> = {
  LayoutDashboard, Building2, Users, FileText, Link2, Tags, Settings, BookOpen,
  ClipboardList, LogOut, ExternalLink, Image, Shield, ShieldCheck, FileCode,
  Sparkles, CalendarClock, CalendarDays, FolderOpen, Globe, Target, GitBranch, Newspaper,
  UserCircle, Palette, AlertTriangle, Package, Book, CheckSquare, Menu, MessageSquare, LayoutTemplate, Lightbulb, Megaphone, Upload, Rocket, Send, GitMerge, BarChart2, Rss, Layers, Mail,
  Handshake, TrendingUp, Star, Navigation,
};

function IconComponent({ name, className }: { name: string; className?: string }) {
  const Icon = ICON_MAP[name] || Menu;
  return <Icon className={className || "h-4 w-4"} />;
}

const fallbackContentItems = [
  { title: "Dashboard", url: "/admin", icon: "LayoutDashboard" },
  { title: "Content Workflow", url: "/admin/content-workflow", icon: "GitBranch" },
  { title: "Articles", url: "/admin/articles", icon: "FileText" },
  { title: "Press Release", url: "/admin/press-release", icon: "Newspaper" },
  { title: "Companies", url: "/admin/companies", icon: "Building2" },
  { title: "Bulk Import", url: "/admin/bulk-import", icon: "Upload" },
  { title: "People", url: "/admin/people", icon: "Users" },
  { title: "Special Content", url: "/admin/special-content", icon: "Sparkles" },
  { title: "Meetings", url: "/admin/meetings", icon: "CalendarClock" },
  { title: "Events", url: "/admin/events", icon: "CalendarDays" },
];

const fallbackManagementItems = [
  { title: "Relationships", url: "/admin/relationships", icon: "Link2" },
  { title: "Relationship Tags", url: "/admin/relationship-tags", icon: "Tags" },
  { title: "Categories", url: "/admin/categories", icon: "Tags" },
  { title: "Classification Types", url: "/admin/classification-config", icon: "Tag" },
  { title: "Special Assets", url: "/admin/special-content/categories", icon: "FolderOpen" },
  { title: "Media Gallery", url: "/admin/media", icon: "Image" },
  { title: "Image Presets", url: "/admin/image-presets", icon: "Image" },
  { title: "Audit Log", url: "/admin/audit-log", icon: "ClipboardList" },
  { title: "Team", url: "/admin/team", icon: "ShieldCheck" },
  { title: "Users", url: "/admin/users", icon: "UserCircle" },
  { title: "Pages", url: "/admin/pages", icon: "FileText" },
  { title: "Site Content", url: "/admin/site-content", icon: "Globe" },
  { title: "Page Sections", url: "/admin/page-sections", icon: "Layers" },
  { title: "Conventions", url: "/admin/conventions", icon: "Tags" },
  { title: "Glossary", url: "/admin/glossary", icon: "BookOpen" },
  { title: "Content Styles", url: "/admin/content-styles", icon: "Palette" },
  { title: "Challenges", url: "/admin/challenge-categories", icon: "AlertTriangle" },
  { title: "Editorial Hub", url: "/admin/editorial-hub", icon: "BookOpen" },
  { title: "Review Hub", url: "/admin/review-hub", icon: "Mail" },
  { title: "AI Hub", url: "/admin/ai-hub", icon: "Sparkles" },
  { title: "Insights Hub", url: "/admin/insights-hub", icon: "Lightbulb" },
  { title: "Templates Hub", url: "/admin/templates-hub", icon: "LayoutTemplate" },
  { title: "AI Images", url: "/admin/ai-images", icon: "Image" },
  { title: "Workflow Criteria", url: "/admin/workflow-criteria", icon: "CheckSquare" },
  { title: "Action Cards", url: "/admin/action-card-packs", icon: "Package" },
  { title: "Sponsor Dashboard", url: "/admin/sponsors", icon: "Target" },
  { title: "Sponsorships", url: "/admin/sponsorships", icon: "Building2" },
  { title: "Campaigns", url: "/admin/campaigns", icon: "Megaphone" },
  { title: "House Adverts", url: "/admin/house-adverts", icon: "Image" },
  { title: "Magazine Hub", url: "/admin/magazines", icon: "BookOpen" },
  { title: "Menu Settings", url: "/admin/menu-settings", icon: "Menu" },
  { title: "Settings", url: "/admin/settings", icon: "Settings" },
  { title: "Launch Readiness", url: "/admin/launch-readiness", icon: "Rocket" },
  { title: "Project Docs", url: "/admin/docs", icon: "FileCode" },
  { title: "Features Directory", url: "/admin/features", icon: "Book" },
  // ── SEO & GEO ──
  { title: "Quick-Start Guide", url: "/admin/seo/quick-start", icon: "Rocket" },
  { title: "SEO Submissions", url: "/admin/seo-submissions", icon: "Send" },
  { title: "Redirect Manager", url: "/admin/redirects", icon: "GitMerge" },
  { title: "Internal Links", url: "/admin/seo/internal-links", icon: "Link2" },
  { title: "Search Performance", url: "/admin/seo/search-performance", icon: "BarChart2" },
];

export function AppSidebar() {
  const [location] = useLocation();
  const { user, logout } = useAuth();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const hasInitializedRef = useRef(false);

  const { data: groups = [] } = useQuery<SidebarMenuGroup[]>({
    queryKey: ["/api/admin/sidebar-menu-groups"],
    retry: false,
  });

  const { data: items = [] } = useQuery<SidebarMenuItemType[]>({
    queryKey: ["/api/admin/sidebar-menu-items"],
    retry: false,
  });

  const addArticlesLinkMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/sidebar-menu/add-articles-link");
      return res.json();
    },
    onSuccess: (data: { added: boolean }) => {
      if (data.added) {
        queryClient.invalidateQueries({ queryKey: ["/api/admin/sidebar-menu-items"] });
      }
    },
    onError: (err: Error) => {
      console.error("[sidebar] Failed to ensure Articles link:", err.message);
    },
  });

  useEffect(() => {
    if (hasInitializedRef.current || groups.length === 0) return;
    
    const defaultExpanded = new Set<string>();
    groups.forEach((group) => {
      if (group.isCollapsible) {
        defaultExpanded.add(group.id);
      }
    });
    setExpandedGroups(defaultExpanded);
    hasInitializedRef.current = true;
  }, [groups]);

  useEffect(() => {
    if (items.length === 0 || groups.length === 0) return;
    const hasArticlesLink = items.some((i) => i.url === "/admin/articles");
    if (!hasArticlesLink) {
      addArticlesLinkMutation.mutate();
    }
  }, [items, groups]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const sortedGroups = [...groups]
    .filter((g) => g.isVisible)
    .sort((a, b) => a.displayOrder - b.displayOrder);

  const getItemsForGroup = (groupId: string) =>
    items
      .filter((item) => item.groupId === groupId && item.isVisible)
      .sort((a, b) => a.displayOrder - b.displayOrder);

  const useDbMenu = groups.length > 0 && items.length > 0;

  return (
    <Sidebar>
      <SidebarHeader className="p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <BookOpen className="h-5 w-5" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-semibold">FM World</span>
            <span className="text-xs text-muted-foreground">Admin</span>
          </div>
        </div>
      </SidebarHeader>
      
      <SidebarSeparator />
      
      <SidebarContent>
        {useDbMenu ? (
          sortedGroups.map((group) => {
            const groupItems = getItemsForGroup(group.id);
            const isExpanded = expandedGroups.has(group.id);

            if (group.isCollapsible) {
              return (
                <Collapsible key={group.id} open={isExpanded} onOpenChange={() => toggleGroup(group.id)}>
                  <SidebarGroup>
                    <CollapsibleTrigger asChild>
                      <SidebarGroupLabel className="cursor-pointer flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <IconComponent name={group.icon || "Menu"} className="h-3 w-3" />
                          <span>{group.name}</span>
                        </div>
                        {isExpanded ? (
                          <ChevronDown className="h-3 w-3 shrink-0" />
                        ) : (
                          <ChevronRight className="h-3 w-3 shrink-0" />
                        )}
                      </SidebarGroupLabel>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarGroupContent>
                        <SidebarMenu>
                          {groupItems.map((item) => (
                            <SidebarMenuItem key={item.id}>
                              <SidebarMenuButton
                                asChild
                                isActive={location === item.url || (item.url !== "/admin" && location.startsWith(item.url))}
                              >
                                <Link
                                  href={item.url}
                                  data-testid={`nav-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                  <IconComponent name={item.icon || "FileText"} className="h-4 w-4" />
                                  <span>{item.title}</span>
                                </Link>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          ))}
                        </SidebarMenu>
                      </SidebarGroupContent>
                    </CollapsibleContent>
                  </SidebarGroup>
                </Collapsible>
              );
            }

            return (
              <SidebarGroup key={group.id}>
                <SidebarGroupLabel className="flex items-center gap-2">
                  <IconComponent name={group.icon || "Menu"} className="h-3 w-3" />
                  <span>{group.name}</span>
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {groupItems.map((item) => (
                      <SidebarMenuItem key={item.id}>
                        <SidebarMenuButton
                          asChild
                          isActive={location === item.url || (item.url !== "/admin" && location.startsWith(item.url))}
                        >
                          <Link
                            href={item.url}
                            data-testid={`nav-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            <IconComponent name={item.icon || "FileText"} className="h-4 w-4" />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            );
          })
        ) : (
          <>
            <SidebarGroup>
              <SidebarGroupLabel>Content</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {fallbackContentItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton 
                        asChild 
                        isActive={location === item.url}
                      >
                        <Link href={item.url} data-testid={`nav-${item.title.toLowerCase()}`}>
                          <IconComponent name={item.icon} className="h-4 w-4" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <SidebarGroupLabel>Management</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {fallbackManagementItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton 
                        asChild 
                        isActive={location === item.url}
                      >
                        <Link href={item.url} data-testid={`nav-${item.title.toLowerCase().replace(" ", "-")}`}>
                          <IconComponent name={item.icon} className="h-4 w-4" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}

        <SidebarGroup>
          <SidebarGroupLabel>Public Site</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <a href="/" target="_blank" rel="noopener noreferrer" data-testid="nav-view-site">
                    <ExternalLink className="h-4 w-4" />
                    <span>View Site</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      
      <SidebarFooter className="p-4 space-y-3">
        {import.meta.env.DEV && (
          <SidebarMenu>
            <SidebarMenuItem>
              <DevRoleSwitcher />
            </SidebarMenuItem>
          </SidebarMenu>
        )}
        <div className="flex items-center gap-3 rounded-md bg-sidebar-accent p-3">
          <Avatar className="h-9 w-9">
            <AvatarImage src={user?.profileImageUrl || undefined} />
            <AvatarFallback>
              {user?.firstName?.[0] || user?.email?.[0] || "U"}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-1 flex-col overflow-hidden">
            <Link href="/admin/settings" data-testid="link-user-settings">
              <span className="truncate text-sm font-medium hover:underline">
                {user?.firstName || user?.email || "User"}
              </span>
            </Link>
            <span className="truncate text-xs text-muted-foreground">
              {user?.role?.replace("_", " ") || "Viewer"}
            </span>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => logout()}
            data-testid="button-logout"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
